import { useContext, useMemo, useState } from "react"
import { Check, ChevronDown } from "lucide-react"
import AdHocCommands from "@/api/adHocCommands"
import { XmppContext } from "@/stream/xmppClient"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { cn } from "@/lib/utils"
import { carShortName, nameWithRoute, type CrewRow } from "@/lib/crewBoard"
import { usePendingPick } from "./usePendingPick"
import { useTranslation } from "react-i18next"

type CrewRouteCellProps = {
  row: CrewRow
  routes: string[]
}

/**
 * The vehicle's route, which lives only in its chat's name ("D12 / C54
 * T/SPRINTER / Jan Kowalski"): picking one renames the chat and
 * changes nothing else, so the same route may sit on several vehicles.
 */
export const CrewRouteCell = ({ row, routes }: CrewRouteCellProps) => {
  const { t } = useTranslation()
  const noRoute = t("fleet.crewBoard.none")
  const { client } = useContext(XmppContext)
  const [open, setOpen] = useState(false)
  const { pending, saving, save } = usePendingPick(row.route)

  // A route dropped from the vhost's list still reads in the name; keep it
  // offered so the cell can show what the chat says.
  const options = useMemo(
    () =>
      row.route && !routes.includes(row.route)
        ? [...routes, row.route]
        : routes,
    [routes, row.route],
  )
  const shown = pending ?? row.route
  const chat = row.chat

  const handleSelect = (route: string) => {
    setOpen(false)
    if (!chat || route === shown) return
    void save(route, async () => {
      await AdHocCommands.upsertMuc({
        client,
        mucJid: chat.jid,
        name: nameWithRoute(row, route),
      })
    })
  }

  return (
    // Not modal, like the driver list: a modal list sets the body's
    // pointer-events (see CrewDriverCell).
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-label={t("fleet.crewBoard.routeFor", {
            vehicle: carShortName(row),
          })}
          aria-expanded={open}
          disabled={saving || !chat || row.doubleCrew}
          className="w-full justify-between font-normal"
        >
          <span className={cn("truncate", !shown && "text-muted-foreground")}>
            {shown || noRoute}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[9999] w-[var(--radix-popover-trigger-width)] min-w-[160px] p-0"
        sideOffset={5}
      >
        <Command>
          <CommandInput placeholder={t("fleet.crewBoard.searchRoute")} />
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty>{t("fleet.crewBoard.noRouteFound")}</CommandEmpty>
            <CommandGroup>
              {["", ...options].map((route) => (
                <CommandItem
                  key={route || "none"}
                  value={route || noRoute}
                  onSelect={() => handleSelect(route)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      route === shown ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {route || noRoute}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
